import React, { useState, useMemo, useCallback } from 'react';
import { Card, Spinner, Alert, Button, Row, Col, Dropdown } from 'react-bootstrap';
import { FaSearch, FaListUl } from 'react-icons/fa';
import { formatDate, formatDateTime, formatTime, formatFieldValue as formatFieldValueUtil } from '../../utils/formatUtils';
import { theme } from '../../theme/theme.js';
import { useSearchViewWidgetData, extractPrimitiveValue } from './hooks/useSearchViewWidgetData';
import SearchViewShowAllModal from './SearchViewShowAllModal';
import EditLayoutModal from './EditLayoutModal';
import DeleteConfirmationModal from '../common/DeleteConfirmationModal';
import DataApi from '../../api/DataApi';
import { normalizeAllowedRelatedListActions } from '../../utils/buildRelatedListTableActions';
import './css/DetailView.css';

/**
 * SearchWidget Component — card layout: same per-record actions dropdown as related-list widgets (LayoutWidget).
 */
const SearchWidget = ({
  widget,
  currentRecordId,
  sourceObjectName,
  recordData,
  onRefresh
}) => {
  const [showAllModal, setShowAllModal] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);

  const {
    searchViewConfig,
    records,
    loading,
    error,
    fieldDataTypes,
    fieldMetadata,
    searchObjectName,
    searchObjectLabel,
    widgetConfig,
    selectedFields,
    executeSearch
  } = useSearchViewWidgetData(widget, recordData);

  const allowedActions = useMemo(
    () => normalizeAllowedRelatedListActions(widgetConfig?.actions, searchObjectName),
    [widgetConfig?.actions, searchObjectName]
  );

  const showAllModalTitle = useMemo(() => {
    return (
      searchViewConfig?.view_label ||
      searchViewConfig?.search_view_label ||
      searchObjectLabel ||
      searchObjectName ||
      'Search'
    );
  }, [searchViewConfig, searchObjectLabel, searchObjectName]);

  const searchViewId = widgetConfig?.search_view_id;
  const maxHeight = widgetConfig.max_height || 400;

  const getRecordIdValue = useCallback((record) => {
    if (!record) return null;
    if (record.id && typeof record.id === 'string') {
      return record.id;
    }
    if (record.id && typeof record.id === 'object' && record.id.value) {
      return record.id.value;
    }
    return record.id || null;
  }, []);

  const refreshAfterChange = useCallback(() => {
    executeSearch();
    if (typeof onRefresh === 'function') {
      onRefresh();
    }
  }, [executeSearch, onRefresh]);

  const handleViewRecord = useCallback(
    (record) => {
      const recordId = getRecordIdValue(record);
      if (!recordId || !searchObjectName) return;
      window.open(`/view/${searchObjectName}/${recordId}`, '_blank');
      setActiveDropdown(null);
    },
    [getRecordIdValue, searchObjectName]
  );

  const handleEditRecord = useCallback(
    (record) => {
      const recordId = getRecordIdValue(record);
      if (!recordId) return;
      setEditingRecord({ ...record, id: { value: recordId } });
      setShowEditModal(true);
      setActiveDropdown(null);
    },
    [getRecordIdValue]
  );

  const handleCloneRecord = useCallback(
    (record) => {
      const recordId = getRecordIdValue(record);
      if (!recordId) return;
      setEditingRecord({ ...record, id: { value: recordId } });
      setShowCloneModal(true);
      setActiveDropdown(null);
    },
    [getRecordIdValue]
  );

  const handleDeleteRecord = useCallback(
    (record) => {
      const recordId = getRecordIdValue(record);
      if (!recordId) return;
      setRecordToDelete(record);
      setShowDeleteModal(true);
      setActiveDropdown(null);
    },
    [getRecordIdValue]
  );

  const confirmDeleteRecord = useCallback(async () => {
    if (!recordToDelete || !searchObjectName) return;
    const recordId = getRecordIdValue(recordToDelete);
    if (!recordId) return;
    try {
      const dataApi = new DataApi('object');
      const response = await dataApi.delete(`/${searchObjectName}/${recordId}`);
      if (response.data.success) {
        setShowDeleteModal(false);
        setRecordToDelete(null);
        refreshAfterChange();
      } else {
        alert(`Failed to delete record: ${response.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error deleting record:', err);
      alert(`Error deleting record: ${err.message}`);
    }
  }, [recordToDelete, searchObjectName, getRecordIdValue, refreshAfterChange]);

  const handleModalSaveSuccess = useCallback(() => {
    setShowEditModal(false);
    setShowCloneModal(false);
    setEditingRecord(null);
    refreshAfterChange();
  }, [refreshAfterChange]);

  const handleModalCancel = useCallback(() => {
    setShowEditModal(false);
    setShowCloneModal(false);
    setEditingRecord(null);
  }, []);

  const formatFieldValue = (rawValue, fieldName) => {
    if (rawValue === null || rawValue === undefined || rawValue === '') {
      return '—';
    }

    const metadata = fieldMetadata[fieldName] || {};
    const dataType = (metadata.data_type || fieldDataTypes[fieldName] || 'text').toLowerCase();
    const displayValue =
      typeof rawValue === 'object' && rawValue !== null
        ? extractPrimitiveValue(rawValue.displayValue)
        : null;
    const primitiveValue = extractPrimitiveValue(rawValue);

    if (['lookup', 'reference'].includes(dataType)) {
      return displayValue || primitiveValue || '—';
    }

    if (dataType === 'picklist' || dataType === 'multipicklist') {
      return displayValue || primitiveValue || '—';
    }

    if (dataType === 'date') {
      return primitiveValue ? formatDate(primitiveValue) : '—';
    }
    if (dataType === 'datetime') {
      return primitiveValue ? formatDateTime(primitiveValue) : '—';
    }
    if (dataType === 'time') {
      return primitiveValue ? formatTime(primitiveValue) : '—';
    }

    if (dataType === 'boolean' || dataType === 'checkbox') {
      return primitiveValue ? 'Yes' : 'No';
    }

    const formatted = formatFieldValueUtil({
      fieldName,
      label: metadata.field_label || fieldName,
      type: metadata.data_type || dataType,
      value: primitiveValue,
      displayValue: typeof rawValue === 'object' && rawValue !== null ? rawValue.displayValue : null,
      scale: rawValue?.scale ?? metadata.scale,
      precision: rawValue?.precision ?? metadata.precision,
      displayConfig: rawValue?.displayConfig ?? metadata.displayConfig
    });

    if (formatted !== null && formatted !== undefined && formatted !== '') {
      return formatted;
    }

    return primitiveValue !== null && primitiveValue !== undefined && primitiveValue !== ''
      ? String(primitiveValue)
      : '—';
  };

  if (!searchViewId || !searchViewConfig) {
    return (
      <Card className="mb-3 widget-card">
        <Card.Header className="widget-card-header">
          <h5 className="mb-0">{widget?.widgetLabel || 'Search Widget'}</h5>
        </Card.Header>
        <Card.Body>
          <Alert variant="warning">Search view not configured for this widget.</Alert>
        </Card.Body>
      </Card>
    );
  }

  if (!searchObjectName) {
    return (
      <Card className="mb-3 widget-card">
        <Card.Header className="widget-card-header">
          <h5 className="mb-0">{widget?.widgetLabel || 'Search Widget'}</h5>
        </Card.Header>
        <Card.Body>
          <Alert variant="warning">Search view object not found.</Alert>
        </Card.Body>
      </Card>
    );
  }

  if (error && !loading) {
    return (
      <Card className="mb-3 widget-card">
        <Card.Header className="widget-card-header">
          <h5 className="mb-0">{widget?.widgetLabel || 'Search Widget'}</h5>
        </Card.Header>
        <Card.Body>
          <Alert variant="danger">{error}</Alert>
        </Card.Body>
      </Card>
    );
  }

  let displayFieldsArray = [];
  if (Array.isArray(searchViewConfig.display_fields)) {
    displayFieldsArray = searchViewConfig.display_fields;
  } else if (typeof searchViewConfig.display_fields === 'string') {
    try {
      displayFieldsArray = JSON.parse(searchViewConfig.display_fields);
    } catch (e) {
      displayFieldsArray = searchViewConfig.display_fields.split(',').map((f) => f.trim()).filter((f) => f);
    }
  }

  const displayFields = (selectedFields.length > 0
    ? selectedFields
    : displayFieldsArray).filter((fieldName) => fieldName !== 'id');

  return (
    <Card className="mb-3 widget-card" style={{ width: '100%' }}>
      <Card.Header
        className="widget-card-header"
        style={{
          padding: '12px 16px',
          borderBottom: '1px solid #dee2e6',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#0176d3',
          color: 'rgb(255, 255, 255)'
        }}
      >
        <h5
          className="mb-0"
          style={{
            fontFamily: theme.typography.fontFamily.primary,
            fontWeight: theme.typography.fontWeight.semibold,
            fontSize: theme.typography.fontSize.lg,
            color: 'rgb(255, 255, 255)'
          }}
        >
          {widget?.widgetLabel || 'Search Results'}
        </h5>
        {records.length > 0 && (
          <Button
            variant="link"
            size="sm"
            onClick={() => setShowAllModal(true)}
            style={{ color: 'white', padding: '0 8px', textDecoration: 'none' }}
          >
            View all <FaListUl className="ms-1" />
          </Button>
        )}
      </Card.Header>
      <Card.Body style={{ padding: '1rem', maxHeight: `${maxHeight}px`, overflowY: 'auto' }}>
        {loading ? (
          <div className="text-center py-3">
            <Spinner animation="border" size="sm" />
            <p className="mt-2 mb-0" style={{ fontSize: '0.875rem' }}>Loading search results...</p>
          </div>
        ) : records.length === 0 ? (
          <div className="text-center py-3">
            <FaSearch style={{ fontSize: '2rem', color: '#ccc', marginBottom: '0.5rem' }} />
            <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>No records found</p>
          </div>
        ) : (
          <div className="widget-records" style={{ width: '100%' }}>
            {records.map((record, idx) => {
              const recordId = getRecordIdValue(record);
              const recordIdValue = recordId || `record-${idx}`;

              return (
                <Card
                  key={recordId || idx}
                  className="mb-2 widget-record-card"
                  style={{
                    border: '1px solid #e0e0e0',
                    width: '100%',
                    position: 'relative',
                    transition: 'all 0.2s'
                  }}
                >
                  <Card.Body className="p-3">
                    <div style={{ position: 'absolute', top: '8px', right: '8px', zIndex: 10 }}>
                        <Dropdown
                          onToggle={(isOpen) => {
                            setActiveDropdown(isOpen ? recordIdValue : null);
                          }}
                          show={activeDropdown === recordIdValue}
                          className="widget-actions-dropdown"
                        >
                          <Dropdown.Toggle
                            variant="light"
                            size="sm"
                            id={`search-widget-dropdown-${recordIdValue}`}
                            className="widget-card-dropdown-toggle"
                            style={{
                              padding: '2px 8px',
                              fontSize: '14px',
                              border: '1px solid #dee2e6',
                              backgroundColor: 'white',
                              color: '#6c757d',
                              lineHeight: 1,
                              opacity: activeDropdown === recordIdValue ? 1 : 0,
                              transition: 'opacity 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.opacity = '1';
                            }}
                            onMouseLeave={(e) => {
                              if (activeDropdown !== recordIdValue) {
                                e.currentTarget.style.opacity = '0';
                              }
                            }}
                          >
                            <span style={{ color: '#6c757d', fontWeight: 'bold' }}>&#9660;</span>
                          </Dropdown.Toggle>
                          <Dropdown.Menu align="end" className="widget-dropdown-menu">
                            <Dropdown.Item
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewRecord(record);
                              }}
                            >
                              <i className="fas fa-eye me-2"></i>
                              View
                            </Dropdown.Item>
                            {allowedActions.includes('edit') && (
                              <Dropdown.Item
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditRecord(record);
                                }}
                              >
                                <i className="fas fa-edit me-2"></i>
                                Edit
                              </Dropdown.Item>
                            )}
                            {allowedActions.includes('clone') && (
                              <Dropdown.Item
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCloneRecord(record);
                                }}
                              >
                                <i className="fas fa-clone me-2"></i>
                                Clone
                              </Dropdown.Item>
                            )}
                            {allowedActions.includes('delete') && (
                              <>
                                <Dropdown.Divider />
                                <Dropdown.Item
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteRecord(record);
                                  }}
                                  className="text-danger"
                                >
                                  <i className="fas fa-trash me-2"></i>
                                  Delete
                                </Dropdown.Item>
                              </>
                            )}
                          </Dropdown.Menu>
                        </Dropdown>
                    </div>

                    <Row className="g-2" style={{ paddingRight: '36px' }}>
                      {displayFields && displayFields.length > 0 ? (
                        displayFields.map((fieldName, fieldIdx) => {
                          const fieldValue = record[fieldName];
                          const fieldLabel = fieldMetadata[fieldName]?.field_label || fieldName;

                          return (
                            <Col xs={12} md={6} key={fieldIdx} className="mb-2">
                              <div style={{ marginBottom: '4px' }}>
                                <div
                                  style={{
                                    fontFamily: theme.typography.fontFamily.primary,
                                    fontWeight: theme.typography.fontWeight.medium,
                                    color: theme.colors.textSecondary,
                                    lineHeight: theme.typography.lineHeight.normal,
                                    fontSize: theme.typography.fontSize.lg,
                                    marginBottom: '2px'
                                  }}
                                >
                                  {fieldLabel}
                                </div>
                                <div
                                  style={{
                                    fontFamily: theme.typography.fontFamily.primary,
                                    fontWeight: theme.typography.fontWeight.normal,
                                    color: theme.colors.textPrimary,
                                    lineHeight: theme.typography.lineHeight.normal,
                                    fontSize: theme.typography.fontSize.lg,
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  {formatFieldValue(fieldValue, fieldName)}
                                </div>
                              </div>
                            </Col>
                          );
                        })
                      ) : (
                        <Col xs={12}>
                          <p className="text-muted" style={{ fontSize: theme.typography.fontSize.lg }}>
                            No fields configured for this widget
                          </p>
                        </Col>
                      )}
                    </Row>
                  </Card.Body>
                </Card>
              );
            })}
          </div>
        )}
      </Card.Body>

      {showEditModal && editingRecord && searchObjectName && (
        <EditLayoutModal
          show={showEditModal}
          onHide={handleModalCancel}
          objectName={searchObjectName}
          id={editingRecord.id?.value}
          sourcePage="detail"
          onSave={handleModalSaveSuccess}
          onCancel={handleModalCancel}
          onRefresh={onRefresh}
        />
      )}

      {showCloneModal && editingRecord && searchObjectName && (
        <EditLayoutModal
          show={showCloneModal}
          onHide={handleModalCancel}
          objectName={searchObjectName}
          id={editingRecord.id?.value}
          isClone={true}
          sourcePage="detail"
          onSave={handleModalSaveSuccess}
          onCancel={handleModalCancel}
          onRefresh={onRefresh}
        />
      )}

      <DeleteConfirmationModal
        show={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
          setRecordToDelete(null);
        }}
        onConfirm={confirmDeleteRecord}
        title="Confirm Delete"
        message="Are you sure you want to delete this record?"
        itemName={recordToDelete ? (getRecordIdValue(recordToDelete) || 'this record') : 'this record'}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      <SearchViewShowAllModal
        show={showAllModal}
        onHide={() => setShowAllModal(false)}
        title={showAllModalTitle}
        widget={widget}
        recordData={recordData}
        searchViewConfig={searchViewConfig}
        fieldDataTypes={fieldDataTypes}
        fieldMetadata={fieldMetadata}
        searchObjectName={searchObjectName}
        currentRecordId={currentRecordId}
        sourceObjectName={sourceObjectName}
      />
    </Card>
  );
};

export default SearchWidget;
